import { create, type StateCreator } from 'zustand';
import { persist } from 'zustand/middleware';
import { usePanelRegistryStore } from './panel-registry-store';

// Filter field configuration type
export interface FilterFieldConfig<T> {
  key: keyof T;
  label: string;
  format?: (value: unknown) => string;
}

// Generic filter info result
export interface FilterInfo {
  count: number;
  labels: string[];
}

// Helper to compute active filters info from any filter data
export function computeActiveFiltersInfo<T extends Record<string, unknown>>(
  filters: T | undefined,
  config: readonly FilterFieldConfig<T>[],
): FilterInfo {
  if (!filters) return { count: 0, labels: [] };

  const labels: string[] = [];
  for (const { key, label, format } of config) {
    const value = filters[key];
    // Skip undefined, empty strings, and empty arrays
    if (value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0)) {
      labels.push(`${label}: ${format ? format(value) : value}`);
    }
  }

  return { count: labels.length, labels };
}

// ============================================
// Generic table state interface
// ============================================

export interface TableState<TFilter = Record<string, unknown>> {
  // Search
  searchInput: string;
  setSearchInput: (value: string) => void;
  clearSearch: () => void;

  // View mode
  viewMode: 'list' | 'grid';
  setViewMode: (mode: 'list' | 'grid') => void;

  // Selection
  selectedIds: Set<string>;
  toggleSelect: (id: string) => void;
  selectAll: (ids: string[]) => void;
  clearSelection: () => void;
  isSelected: (id: string) => boolean;

  // Action menu
  openActionMenuId: string | null;
  openActionMenu: (id: string) => void;
  closeActionMenu: () => void;
  toggleActionMenu: (id: string) => void;

  // Filters
  filters: TFilter | undefined;
  setFilters: (filters: TFilter | undefined) => void;
  resetFilters: () => void;

  // Sorting
  sortBy: string | undefined;
  sortOrder: 'asc' | 'desc';
  setSorting: (sortBy: string | undefined, sortOrder?: 'asc' | 'desc') => void;

  // Filter computed values
  getActiveFilterCount: () => number;
  getActiveFilterLabels: () => string[];
  hasActiveFilters: () => boolean;

  // Modals
  isAddModalOpen: boolean;
  isFilterModalOpen: boolean;
  isDeleteModalOpen: boolean;
  openAddModal: () => void;
  closeAddModal: () => void;
  openFilterModal: () => void;
  closeFilterModal: () => void;
  openDeleteModal: () => void;
  closeDeleteModal: () => void;

  // Delete target
  deleteTargetId: string | null;
  setDeleteTarget: (id: string | null) => void;

  // Reset all state
  reset: () => void;
}

// Options for creating a table store with default sort configuration
export interface CreateTableStoreOptions {
  defaultSortBy?: string;
  defaultSortOrder?: 'asc' | 'desc';
  /** Store name used for panel registry (e.g., 'contact', 'sequence') */
  storeName?: string;
  /** Default view mode (defaults to 'list') */
  defaultViewMode?: 'list' | 'grid';
  /** Persist config — when set, wraps the store with zustand persist middleware (only persists viewMode) */
  persist?: {
    /** localStorage key */
    name: string;
  };
}

// Factory function to create entity-specific table stores
export function createTableStore<TFilter extends Record<string, unknown>>(
  filterConfig: readonly FilterFieldConfig<TFilter>[] = [],
  options?: CreateTableStoreOptions,
) {
  const defaultSortBy = options?.defaultSortBy;
  const defaultSortOrder = options?.defaultSortOrder ?? 'desc';
  const defaultViewMode = options?.defaultViewMode ?? 'list';
  const storeName = options?.storeName;

  // Panel registry helpers
  const registerPanel = (suffix: string) => {
    if (storeName) {
      usePanelRegistryStore.getState().registerPanel(`${storeName}-table-${suffix}`);
    }
  };
  const unregisterPanel = (suffix: string) => {
    if (storeName) {
      usePanelRegistryStore.getState().unregisterPanel(`${storeName}-table-${suffix}`);
    }
  };

  const storeCreator: StateCreator<TableState<TFilter>> = (set, get) => ({
    // Search
    searchInput: '',
    setSearchInput: (value) => set({ searchInput: value }),
    clearSearch: () => set({ searchInput: '' }),

    // View mode
    viewMode: defaultViewMode,
    setViewMode: (mode) => set({ viewMode: mode }),

    // Selection
    selectedIds: new Set<string>(),
    toggleSelect: (id) =>
      set((state) => {
        const newSet = new Set(state.selectedIds);
        if (newSet.has(id)) {
          newSet.delete(id);
        } else {
          newSet.add(id);
        }
        return { selectedIds: newSet };
      }),
    selectAll: (ids) => set({ selectedIds: new Set(ids) }),
    clearSelection: () => set({ selectedIds: new Set() }),
    isSelected: (id) => get().selectedIds.has(id),

    // Action menu
    openActionMenuId: null,
    openActionMenu: (id) => set({ openActionMenuId: id }),
    closeActionMenu: () => set({ openActionMenuId: null }),
    toggleActionMenu: (id) =>
      set((state) => ({
        openActionMenuId: state.openActionMenuId === id ? null : id,
      })),

    // Filters
    filters: undefined,
    setFilters: (filters) => set({ filters }),
    resetFilters: () => set({ filters: undefined }),

    // Sorting - use defaults from options
    sortBy: defaultSortBy,
    sortOrder: defaultSortOrder,
    setSorting: (sortBy, sortOrder = 'desc') => {
      set((state) => {
        // If clicking the same column, toggle order; otherwise set new column with desc order
        if (state.sortBy === sortBy) {
          return {
            sortBy,
            sortOrder: state.sortOrder === 'asc' ? 'desc' : 'asc',
          };
        }
        return { sortBy, sortOrder };
      });
    },

    // Filter computed values
    getActiveFilterCount: () => {
      const { filters } = get();
      return computeActiveFiltersInfo(filters, filterConfig).count;
    },
    getActiveFilterLabels: () => {
      const { filters } = get();
      return computeActiveFiltersInfo(filters, filterConfig).labels;
    },
    hasActiveFilters: () => get().getActiveFilterCount() > 0,

    // Modals
    isAddModalOpen: false,
    isFilterModalOpen: false,
    isDeleteModalOpen: false,
    openAddModal: () => {
      registerPanel('add');
      set({ isAddModalOpen: true });
    },
    closeAddModal: () => {
      unregisterPanel('add');
      set({ isAddModalOpen: false });
    },
    openFilterModal: () => {
      registerPanel('filter');
      set({ isFilterModalOpen: true });
    },
    closeFilterModal: () => {
      unregisterPanel('filter');
      set({ isFilterModalOpen: false });
    },
    openDeleteModal: () => {
      registerPanel('delete');
      set({ isDeleteModalOpen: true });
    },
    closeDeleteModal: () => {
      unregisterPanel('delete');
      set({ isDeleteModalOpen: false, deleteTargetId: null });
    },

    // Delete target
    deleteTargetId: null,
    setDeleteTarget: (id) => set({ deleteTargetId: id }),

    // Reset all state - restore to defaults
    reset: () =>
      set({
        searchInput: '',
        viewMode: defaultViewMode,
        selectedIds: new Set(),
        openActionMenuId: null,
        filters: undefined,
        sortBy: defaultSortBy,
        sortOrder: defaultSortOrder,
        isAddModalOpen: false,
        isFilterModalOpen: false,
        isDeleteModalOpen: false,
        deleteTargetId: null,
      }),
  });

  if (options?.persist) {
    return create<TableState<TFilter>>()(
      persist(storeCreator, {
        name: options.persist.name,
        partialize: (state) => ({
          viewMode: state.viewMode,
        }),
      }),
    );
  }

  return create<TableState<TFilter>>(storeCreator);
}
